/** Milestone 3 additions (docs/API.md "Milestone 3 additions"). */
import { z } from 'zod'
import { Address, Page } from '../../primitives.js'
import { Ok } from '../common.js'
import { PaymentIntentResult } from '../billing.js'
import { AcknowledgeRisk, CandlesQuery, CostEstimate, CostEstimateQuery, CreatorProfile, Holding, Invoice, InvoicesQuery, MyAgent, MyAgentsQuery, OgData, PricingTier, RiskAcknowledgement, RiskDisclosure, SetAgentPricing, TokenCandles, TrendingItem, TrendingQuery } from '../home.js'
import { CreateWebhook, InboxMessage, InboxQuery, MarkedRead, MarkNotificationsRead, Notification, NotificationPreference, NotificationsQuery, PatchNotificationPreferences, UnreadCount, Webhook } from '../notifications.js'
import { AdminAgentRow, AdminAgentsQuery, AdminJob, AdminJobsQuery, AdminOverview, AdminUnreconciledFee, AdminUserRow, AdminUsersQuery } from '../admin.js'
import { TokenEarnings, TokenEarningsQuery } from '../tokens.js'
import { HandleParams, IdParams, BrainIdParams } from './params.js'
import { defineRoute, type RouteDef } from './define.js'

export const milestone3Routes = {
  // --- Milestone 3 (contract step; docs/API.md "Milestone 3 additions") ---
  // Owner home and creator profiles
  listMyAgents: defineRoute({
    method: 'GET',
    path: '/me/agents',
    auth: 'session',
    summary: 'My agents with status, version, credits today, last tick and open issues',
    query: MyAgentsQuery,
    response: Page(MyAgent),
  }),
  listMyHoldings: defineRoute({
    method: 'GET',
    path: '/me/holdings',
    auth: 'session',
    summary: 'Agent tokens held by my linked wallets, with value and claimable rewards',
    response: z.array(Holding),
  }),
  getCreatorProfile: defineRoute({
    method: 'GET',
    path: '/u/:handle',
    auth: 'public',
    summary: 'Creator profile: user card, agents, tokens, aggregate stats',
    params: HandleParams,
    response: CreatorProfile,
  }),
  getBrainEstimate: defineRoute({
    method: 'GET',
    path: '/brains/:id/estimate',
    auth: 'public',
    summary: 'Cost estimate from the current rate card (brain id URL-encoded, e.g. anthropic%2Fclaude-sonnet-5)',
    params: BrainIdParams,
    query: CostEstimateQuery,
    response: CostEstimate,
  }),

  // Risk
  acknowledgeRisk: defineRoute({
    method: 'POST',
    path: '/me/risk-acknowledgement',
    auth: 'session',
    summary: 'Acknowledge the risk disclosure version (required before authorizing an agent or resuming a permission, else RISK_ACK_REQUIRED)',
    body: AcknowledgeRisk,
    status: 201,
    response: RiskAcknowledgement,
  }),
  getRiskDisclosure: defineRoute({ method: 'GET', path: '/risk-disclosure', auth: 'public', summary: 'Current risk disclosure version and text', response: RiskDisclosure }),

  // Subscriptions, pricing, invoices
  getAgentPricing: defineRoute({
    method: 'GET',
    path: '/agents/:handle/pricing',
    auth: 'public',
    summary: 'Subscription tiers set by the creator',
    params: HandleParams,
    response: z.array(PricingTier),
  }),
  setAgentPricing: defineRoute({
    method: 'PATCH',
    path: '/agents/:handle/pricing',
    auth: 'session',
    owner: true,
    summary: 'Replace subscription tiers within the platform bounds (AGENT_PRICING_BOUNDS)',
    params: HandleParams,
    body: SetAgentPricing,
    response: z.array(PricingTier),
  }),
  listMyInvoices: defineRoute({
    method: 'GET',
    path: '/me/invoices',
    auth: 'session',
    summary: 'Invoices (performance fees and renewals), newest first',
    query: InvoicesQuery,
    response: z.array(Invoice),
  }),
  payMyInvoice: defineRoute({
    method: 'POST',
    path: '/me/invoices/:id/pay',
    auth: 'session',
    summary: 'Pay an invoice: payment intent + wallet txs',
    params: IdParams,
    status: 201,
    response: PaymentIntentResult,
  }),

  // Notifications
  listMyNotifications: defineRoute({
    method: 'GET',
    path: '/me/notifications',
    auth: 'session',
    summary: 'Notifications, newest first',
    query: NotificationsQuery,
    response: Page(Notification),
  }),
  getMyUnreadCount: defineRoute({ method: 'GET', path: '/me/notifications/unread-count', auth: 'session', summary: 'Unread notifications', response: UnreadCount }),
  markMyNotificationsRead: defineRoute({
    method: 'POST',
    path: '/me/notifications/read',
    auth: 'session',
    summary: 'Mark notifications read (ids or all)',
    body: MarkNotificationsRead,
    response: MarkedRead,
  }),
  getMyNotificationPreferences: defineRoute({
    method: 'GET',
    path: '/me/notification-preferences',
    auth: 'session',
    summary: 'Delivery preferences per notification kind',
    response: z.array(NotificationPreference),
  }),
  patchMyNotificationPreferences: defineRoute({
    method: 'PATCH',
    path: '/me/notification-preferences',
    auth: 'session',
    summary: 'Update delivery preferences per kind',
    body: PatchNotificationPreferences,
    response: z.array(NotificationPreference),
  }),
  streamNotifications: defineRoute({
    method: 'GET',
    path: '/me/notifications/stream',
    auth: 'session',
    summary: 'SSE of Notification for the caller, heartbeat every 15 s',
    response: Notification,
    stream: true,
  }),

  // Discovery and sharing
  getTrending: defineRoute({ method: 'GET', path: '/trending', auth: 'public', summary: 'Agents and tokens by recent activity and volume', query: TrendingQuery, response: z.array(TrendingItem) }),
  getAgentOg: defineRoute({
    method: 'GET',
    path: '/agents/:handle/og',
    auth: 'public',
    summary: 'Open Graph data for the per-agent link preview',
    params: HandleParams,
    response: OgData,
  }),
  getSkillFile: defineRoute({
    method: 'GET',
    path: '/connect/skill.md',
    auth: 'public',
    summary: 'Agent skill file (markdown) generated from the route map',
    responseType: 'markdown',
    response: z.string(),
  }),

  // External agents
  listAgentInbox: defineRoute({
    method: 'GET',
    path: '/agent/inbox',
    auth: 'agent_key',
    summary: 'Inbox: permission changes, budget, revisions, system',
    query: InboxQuery,
    response: Page(InboxMessage),
  }),
  ackAgentInbox: defineRoute({
    method: 'POST',
    path: '/agent/inbox/:id/ack',
    auth: 'agent_key',
    summary: 'Acknowledge an inbox message (status processed)',
    params: IdParams,
    response: InboxMessage,
  }),
  listAgentWebhooks: defineRoute({ method: 'GET', path: '/agent/webhooks', auth: 'agent_key', summary: 'Webhooks', response: z.array(Webhook) }),
  createAgentWebhook: defineRoute({
    method: 'POST',
    path: '/agent/webhooks',
    auth: 'agent_key',
    summary: 'Add a webhook (HMAC-SHA256 signed deliveries, retried with backoff)',
    body: CreateWebhook,
    status: 201,
    response: Webhook,
  }),
  deleteAgentWebhook: defineRoute({ method: 'DELETE', path: '/agent/webhooks/:id', auth: 'agent_key', summary: 'Remove a webhook', params: IdParams, response: Ok }),

  // Tokens
  getTokenCandles: defineRoute({
    method: 'GET',
    path: '/tokens/:address/candles',
    auth: 'public',
    summary: 'Price candles in quote units per token, oldest first',
    params: z.object({ address: Address }),
    query: CandlesQuery,
    response: TokenCandles,
  }),
  getTokenEarnings: defineRoute({
    method: 'GET',
    path: '/tokens/:address/earnings',
    auth: 'public',
    summary: 'Holder earnings: fees collected by category, 40/40/20 allocation, funded, paid and unclaimed per epoch, next close',
    params: z.object({ address: Address }),
    query: TokenEarningsQuery,
    response: TokenEarnings,
  }),

  // Admin
  adminOverview: defineRoute({
    method: 'GET',
    path: '/admin/overview',
    auth: 'admin',
    summary: 'Counts, queue depth, indexer lag, model spend today',
    response: AdminOverview,
  }),
  adminListAgents: defineRoute({ method: 'GET', path: '/admin/agents', auth: 'admin', summary: 'All agents', query: AdminAgentsQuery, response: Page(AdminAgentRow) }),
  adminListUsers: defineRoute({ method: 'GET', path: '/admin/users', auth: 'admin', summary: 'All users', query: AdminUsersQuery, response: Page(AdminUserRow) }),
  adminListJobs: defineRoute({ method: 'GET', path: '/admin/jobs', auth: 'admin', summary: 'Jobs by status and kind', query: AdminJobsQuery, response: Page(AdminJob) }),
  adminRetryJob: defineRoute({ method: 'POST', path: '/admin/jobs/:id/retry', auth: 'admin', summary: 'Requeue a failed, dead or cancelled job', params: IdParams, response: AdminJob }),
  adminCancelJob: defineRoute({ method: 'POST', path: '/admin/jobs/:id/cancel', auth: 'admin', summary: 'Cancel a queued job', params: IdParams, response: AdminJob }),
  adminListUnreconciledFees: defineRoute({
    method: 'GET',
    path: '/admin/fees/unreconciled',
    auth: 'admin',
    summary: 'Fee ledger rows not yet reconciled',
    response: z.array(AdminUnreconciledFee),
  }),
} as const satisfies Record<string, RouteDef>
